import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import { Building2, FileText, Upload, ChevronDown, AlertCircle, CheckCircle, Clock, FolderOpen, Trash2, Download, Paperclip } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = ["Title Deed", "Sale Deed", "Registration Certificate", "Tax Receipt", "NOC", "Identity Proof", "Other"];

const STATUS_COLORS = {
  pending:  "bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400",
  approved: "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400",
  rejected: "bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400",
};
const STATUS_ICONS = {
  pending:  <Clock size={12} />,
  approved: <CheckCircle size={12} />,
  rejected: <AlertCircle size={12} />,
};

export default function OwnerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myUnits, setMyUnits] = useState([]);
  const [requests, setRequests] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [selectedUnitForReq, setSelectedUnitForReq] = useState("");
  const [reqField, setReqField] = useState("occupancy_status");
  const [reqValue, setReqValue] = useState("");
  const [reqNotes, setReqNotes] = useState("");
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  // Document upload state
  const [docUploadUnit, setDocUploadUnit] = useState(null); // unit_id or null
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [docLabel, setDocLabel] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [docUploading, setDocUploading] = useState(false);

  // Redirect if not owner
  useEffect(() => {
    if (!user) { navigate("/owner/login"); return; }
    if (user.role !== "owner" && user.role !== "admin") { navigate("/"); return; }
  }, [user, navigate]);

  const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : {};

  useEffect(() => {
    if (!user) return;
    Promise.all([
      axios.get(`${API}/owner/units`, { headers }),
      axios.get(`${API}/owner/change-requests`, { headers }),
      axios.get(`${API}/owner/documents`, { headers }),
    ]).then(([unitsRes, reqsRes, docsRes]) => {
      setMyUnits(unitsRes.data);
      setRequests(reqsRes.data);
      setDocuments(docsRes.data);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [user]);

  const refreshDocs = async () => {
    try {
      const { data } = await axios.get(`${API}/owner/documents`, { headers });
      setDocuments(data);
    } catch { /* ignore */ }
  };

  const handleDocUpload = async (e) => {
    e.preventDefault();
    if (!docUploadUnit || !docFile) return;
    if (docFile.size > 10 * 1024 * 1024) {
      toast.error("File exceeds 10 MB limit");
      return;
    }
    setDocUploading(true);
    try {
      const form = new FormData();
      form.append("file", docFile);
      const qs = new URLSearchParams({
        unit_id: docUploadUnit,
        document_type: docType,
        label: docLabel,
      });
      await axios.post(`${API}/owner/documents?${qs}`, form, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Document uploaded to ${docUploadUnit}`);
      setDocUploadUnit(null);
      setDocType(DOC_TYPES[0]);
      setDocLabel("");
      setDocFile(null);
      await refreshDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setDocUploading(false);
    }
  };

  const handleDocDelete = async (docId, label) => {
    if (!window.confirm(`Remove document "${label}"?`)) return;
    try {
      await axios.delete(`${API}/owner/documents/${docId}`, { headers });
      toast.success("Document removed");
      await refreshDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleDocDownload = async (d) => {
    try {
      const resp = await axios.get(`${API}/files/${d.storage_path}`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], {
        type: d.content_type || resp.headers["content-type"] || "application/octet-stream",
      }));
      const a  = document.createElement("a");
      a.href     = url;
      a.download = d.original_filename || `${d.document_type}.bin`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const docsByUnit = documents.reduce((acc, d) => {
    (acc[d.unit_id] = acc[d.unit_id] || []).push(d);
    return acc;
  }, {});

  const handleFileUpload = async (files) => {
    const paths = [];
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await axios.post(`${API}/upload`, form, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
        paths.push(data.path);
        toast.success(`Uploaded: ${file.name}`);
      } catch {
        toast.error(`Failed to upload: ${file.name}`);
      }
    }
    return paths;
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!selectedUnitForReq || !reqValue) return;
    setUploading(true);
    try {
      let storagePaths = [];
      if (evidenceFiles.length > 0) {
        storagePaths = await handleFileUpload(evidenceFiles);
      }
      await axios.post(`${API}/change-requests`, {
        unit_id: selectedUnitForReq,
        requested_fields: { [reqField]: reqValue },
        notes: reqNotes,
        evidence_storage_paths: storagePaths,
      }, { headers });
      toast.success("Change request submitted successfully");
      setShowRequestForm(false);
      setReqField("occupancy_status");
      setReqValue("");
      setReqNotes("");
      setEvidenceFiles([]);
      // Refresh requests
      const { data } = await axios.get(`${API}/owner/change-requests`, { headers });
      setRequests(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="w-8 h-8 border-2 border-[#9C7C54] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="owner-dashboard">
      {/* Header */}
      <div className="mb-8">
        <div className="text-xs tracking-[0.25em] uppercase text-[#9C7C54] font-semibold mb-2">Owner Dashboard</div>
        <h1 className="font-heading text-3xl font-light text-[#1A1A1A] dark:text-[#EAEAEA]">
          Welcome, {user?.name}
        </h1>
        <p className="text-sm text-[#737373] mt-1">
          {myUnits.length} linked unit{myUnits.length !== 1 ? "s" : ""}
          {myUnits.length > 1 && (
            <span className="ml-2 text-[10px] tracking-wide uppercase bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-sm">
              Multi-unit account
            </span>
          )}
        </p>
      </div>

      {/* My Units */}
      <section className="mb-8">
        <h2 className="font-heading text-xl font-light text-[#1A1A1A] dark:text-[#EAEAEA] mb-4 flex items-center gap-2">
          <Building2 size={18} className="text-[#9C7C54]" />
          My Units
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {myUnits.map((unit) => (
            <div
              key={unit.unit_id}
              data-testid={`owner-unit-card-${unit.unit_id}`}
              className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] p-6 hover:border-[#9C7C54] transition-colors duration-200 animate-slide-up"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-[#9C7C54] font-semibold">Unit</div>
                  <div className="font-heading text-2xl font-light text-[#1A1A1A] dark:text-[#EAEAEA]">{unit.unit_id}</div>
                </div>
                <span
                  className={`text-[10px] font-semibold px-2 py-1 rounded-sm flex items-center gap-1 ${
                    unit.occupancy_status === "occupied" ? "badge-occupied" : "badge-vacant"
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${unit.occupancy_status === "occupied" ? "bg-emerald-500" : "bg-gray-400"}`} />
                  {unit.occupancy_status}
                </span>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#737373]">Floor</span>
                  <span className="text-[#1A1A1A] dark:text-[#EAEAEA] font-medium">
                    {unit.floor === "GF" ? "Ground Floor" : `First Floor · Block ${unit.wing}`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#737373]">Size</span>
                  <span className="text-[#1A1A1A] dark:text-[#EAEAEA] font-medium">
                    {unit.size_sqft > 0 ? `${unit.size_sqft.toFixed(2)} sq ft` : "—"}
                  </span>
                </div>
                {unit.owners.map((o, i) => (
                  <div key={i} className="flex justify-between">
                    <span className="text-[#737373]">Owner {unit.owners.length > 1 ? i + 1 : ""}</span>
                    <div className="text-right">
                      <div className="text-[#1A1A1A] dark:text-[#EAEAEA] font-medium">{o.name}</div>
                      <div className="text-[11px] font-mono text-[#737373]">{o.phone}</div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                data-testid={`submit-request-btn-${unit.unit_id}`}
                onClick={() => { setSelectedUnitForReq(unit.unit_id); setShowRequestForm(true); }}
                className="mt-4 w-full py-2 border border-[#9C7C54] text-[#9C7C54] text-xs tracking-wide hover:bg-[#9C7C54] hover:text-white transition-colors rounded-sm"
              >
                Submit Change Request
              </button>
              <button
                data-testid={`upload-doc-btn-${unit.unit_id}`}
                onClick={() => { setDocUploadUnit(unit.unit_id); setDocType(DOC_TYPES[0]); setDocLabel(""); setDocFile(null); }}
                className="mt-2 w-full py-2 flex items-center justify-center gap-1.5 text-xs tracking-wide text-[#737373] hover:text-[#9C7C54] transition-colors"
              >
                <Paperclip size={12} /> Upload Document
                {docsByUnit[unit.unit_id]?.length > 0 && (
                  <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-[#9C7C54]/15 text-[#9C7C54]">
                    {docsByUnit[unit.unit_id].length}
                  </span>
                )}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Legal Documents Section */}
      <section className="mb-8" data-testid="documents-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-light text-[#1A1A1A] dark:text-[#EAEAEA] flex items-center gap-2">
            <FolderOpen size={18} className="text-[#9C7C54]" />
            My Legal Documents
            <span className="text-xs text-[#737373] font-sans">({documents.length})</span>
          </h2>
        </div>

        {/* Upload Form */}
        {docUploadUnit && (
          <form
            onSubmit={handleDocUpload}
            className="bg-white dark:bg-[#1A1D24] border border-[#9C7C54]/40 p-6 mb-4 animate-slide-up rounded-sm"
            data-testid="doc-upload-form"
          >
            <h3 className="font-heading text-lg font-light mb-4 text-[#1A1A1A] dark:text-[#EAEAEA]">
              Upload document for <span className="text-[#9C7C54]">{docUploadUnit}</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase text-[#737373] mb-1.5">Document Type</label>
                <div className="relative">
                  <select
                    data-testid="doc-type-select"
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                    className="w-full px-3 py-2.5 border border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#0F1115] text-sm text-[#1A1A1A] dark:text-[#EAEAEA] focus:outline-none focus:ring-1 focus:ring-[#9C7C54] appearance-none rounded-sm"
                  >
                    {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase text-[#737373] mb-1.5">Label (optional)</label>
                <input
                  data-testid="doc-label-input"
                  type="text"
                  value={docLabel}
                  onChange={(e) => setDocLabel(e.target.value)}
                  placeholder="e.g. Registered on Mar 2019"
                  className="w-full px-3 py-2.5 border border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#0F1115] text-sm text-[#1A1A1A] dark:text-[#EAEAEA] focus:outline-none focus:ring-1 focus:ring-[#9C7C54] rounded-sm"
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-xs tracking-[0.15em] uppercase text-[#737373] mb-1.5">File (PDF / image, max 10 MB)</label>
              <input
                data-testid="doc-file-input"
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                required
                className="w-full text-sm text-[#737373] file:mr-3 file:py-1.5 file:px-3 file:border file:border-[#9C7C54] file:text-[#9C7C54] file:text-xs file:bg-transparent file:cursor-pointer hover:file:bg-[#9C7C54] hover:file:text-white file:transition-colors"
              />
              {docFile && (
                <p className="text-[11px] text-[#737373] mt-1">
                  {docFile.name} — {(docFile.size / 1024).toFixed(0)} KB
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={docUploading || !docFile}
                data-testid="doc-upload-submit-btn"
                className="px-5 py-2 bg-[#9C7C54] text-white text-sm tracking-wide hover:bg-[#7a5f3e] disabled:opacity-50 transition-colors rounded-sm"
              >
                {docUploading ? "Uploading…" : "Upload"}
              </button>
              <button
                type="button"
                onClick={() => setDocUploadUnit(null)}
                data-testid="doc-upload-cancel-btn"
                className="px-5 py-2 border border-[#E5E5E5] dark:border-[#272A30] text-sm text-[#737373] hover:border-[#9C7C54] transition-colors rounded-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Documents list grouped by unit */}
        {documents.length === 0 ? (
          <div className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] p-8 text-center text-[#737373] rounded-sm">
            <FolderOpen size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No documents uploaded yet</p>
            <p className="text-xs mt-1">Click &quot;Upload Document&quot; on any unit card above.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(docsByUnit).map(([unitId, docs]) => (
              <div
                key={unitId}
                className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] rounded-sm overflow-hidden"
                data-testid={`docs-group-${unitId}`}
              >
                <div className="px-4 py-2.5 bg-[#F9F8F6] dark:bg-[#0F1115] border-b border-[#E5E5E5] dark:border-[#272A30] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 size={13} className="text-[#9C7C54]" />
                    <span className="text-sm font-semibold text-[#1A1A1A] dark:text-[#EAEAEA]">{unitId}</span>
                    <span className="text-[10px] text-[#737373]">· {docs.length} file{docs.length !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <div className="divide-y divide-[#E5E5E5] dark:divide-[#272A30]">
                  {docs.map((d) => (
                    <div
                      key={d.id}
                      data-testid={`doc-row-${d.id}`}
                      className="px-4 py-3 flex items-center gap-3 hover:bg-[#F9F8F6] dark:hover:bg-[#0F1115]/60 transition-colors"
                    >
                      <FileText size={16} className="text-[#9C7C54] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-[#1A1A1A] dark:text-[#EAEAEA] truncate">
                            {d.document_type}
                          </span>
                          {d.label && (
                            <span className="text-[10px] text-[#737373] italic">— {d.label}</span>
                          )}
                        </div>
                        <div className="text-[11px] text-[#737373] mt-0.5 truncate">
                          {d.original_filename} · {(d.size_bytes / 1024).toFixed(0)} KB · {new Date(d.uploaded_at).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDocDownload(d)}
                        data-testid={`doc-download-${d.id}`}
                        className="p-2 text-[#737373] hover:text-[#9C7C54] transition-colors"
                        title="Download"
                      >
                        <Download size={14} />
                      </button>
                      <button
                        onClick={() => handleDocDelete(d.id, d.label || d.document_type)}
                        data-testid={`doc-delete-${d.id}`}
                        className="p-2 text-[#737373] hover:text-red-600 transition-colors"
                        title="Remove"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Change Request History */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-light text-[#1A1A1A] dark:text-[#EAEAEA] flex items-center gap-2">
            <FileText size={18} className="text-[#9C7C54]" />
            Change Requests
          </h2>
          <button
            data-testid="new-request-btn"
            onClick={() => setShowRequestForm(!showRequestForm)}
            className="flex items-center gap-2 px-4 py-2 bg-[#9C7C54] text-white text-xs tracking-wide hover:bg-[#7a5f3e] transition-colors rounded-sm"
          >
            <Upload size={13} /> New Request
          </button>
        </div>

        {/* Request form */}
        {showRequestForm && (
          <div className="bg-white dark:bg-[#1A1D24] border border-[#9C7C54]/40 p-6 mb-4 animate-slide-up">
            <h3 className="font-heading text-lg font-light mb-4 text-[#1A1A1A] dark:text-[#EAEAEA]">Submit Change Request</h3>
            <form onSubmit={handleSubmitRequest} data-testid="change-request-form" className="space-y-4">
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase text-[#737373] mb-1.5">Unit</label>
                <div className="relative">
                  <select
                    data-testid="req-unit-select"
                    value={selectedUnitForReq}
                    onChange={(e) => setSelectedUnitForReq(e.target.value)}
                    className="w-full px-3 py-2.5 border border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#0F1115] text-sm text-[#1A1A1A] dark:text-[#EAEAEA] focus:outline-none focus:ring-1 focus:ring-[#9C7C54] appearance-none rounded-sm"
                    required
                  >
                    <option value="">Select unit</option>
                    {myUnits.map((u) => (
                      <option key={u.unit_id} value={u.unit_id}>{u.unit_id}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase text-[#737373] mb-1.5">Field to Change</label>
                  <div className="relative">
                    <select
                      data-testid="req-field-select"
                      value={reqField}
                      onChange={(e) => setReqField(e.target.value)}
                      className="w-full px-3 py-2.5 border border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#0F1115] text-sm text-[#1A1A1A] dark:text-[#EAEAEA] focus:outline-none focus:ring-1 focus:ring-[#9C7C54] appearance-none rounded-sm"
                    >
                      <option value="occupancy_status">Occupancy Status</option>
                      <option value="size_sqft">Unit Size</option>
                      <option value="owner_name">Owner Name</option>
                      <option value="phone">Contact Number</option>
                      <option value="other">Other</option>
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase text-[#737373] mb-1.5">New Value</label>
                  <input
                    data-testid="req-value-input"
                    type="text"
                    value={reqValue}
                    onChange={(e) => setReqValue(e.target.value)}
                    placeholder="New value…"
                    className="w-full px-3 py-2.5 border border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#0F1115] text-sm text-[#1A1A1A] dark:text-[#EAEAEA] focus:outline-none focus:ring-1 focus:ring-[#9C7C54] rounded-sm"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs tracking-[0.15em] uppercase text-[#737373] mb-1.5">Notes</label>
                <textarea
                  data-testid="req-notes-input"
                  value={reqNotes}
                  onChange={(e) => setReqNotes(e.target.value)}
                  placeholder="Additional context for the admin…"
                  rows={3}
                  className="w-full px-3 py-2.5 border border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#0F1115] text-sm text-[#1A1A1A] dark:text-[#EAEAEA] focus:outline-none focus:ring-1 focus:ring-[#9C7C54] resize-none rounded-sm"
                />
              </div>

              <div>
                <label className="block text-xs tracking-[0.15em] uppercase text-[#737373] mb-1.5">Evidence Files</label>
                <input
                  data-testid="req-evidence-input"
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={(e) => setEvidenceFiles(Array.from(e.target.files))}
                  className="w-full text-sm text-[#737373] file:mr-3 file:py-1.5 file:px-3 file:border file:border-[#9C7C54] file:text-[#9C7C54] file:text-xs file:bg-transparent file:cursor-pointer hover:file:bg-[#9C7C54] hover:file:text-white file:transition-colors"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  data-testid="req-submit-btn"
                  type="submit"
                  disabled={uploading}
                  className="px-6 py-2.5 bg-[#9C7C54] text-white text-sm tracking-wide hover:bg-[#7a5f3e] disabled:opacity-50 transition-colors rounded-sm"
                >
                  {uploading ? "Submitting…" : "Submit Request"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRequestForm(false)}
                  className="px-6 py-2.5 border border-[#E5E5E5] dark:border-[#272A30] text-sm text-[#737373] hover:border-[#9C7C54] transition-colors rounded-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Requests list */}
        {requests.length === 0 ? (
          <div className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] p-8 text-center text-[#737373]">
            <FileText size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No change requests submitted yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((req) => (
              <div
                key={req.id}
                data-testid={`request-row-${req.id}`}
                className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] p-4 flex items-start justify-between gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-[#1A1A1A] dark:text-[#EAEAEA]">{req.unit_id}</span>
                    <span className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-sm ${STATUS_COLORS[req.status] || ""}`}>
                      {STATUS_ICONS[req.status]}
                      {req.status}
                    </span>
                  </div>
                  <p className="text-xs text-[#737373] truncate">
                    {Object.entries(req.requested_fields || {}).map(([k, v]) => `${k}: ${v}`).join(", ")}
                  </p>
                  {req.admin_notes && (
                    <p className="text-xs text-[#9C7C54] mt-1">Admin: {req.admin_notes}</p>
                  )}
                </div>
                <div className="text-[10px] text-[#737373] whitespace-nowrap flex-shrink-0">
                  {req.created_at ? new Date(req.created_at).toLocaleDateString() : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
